import type { ReactNode } from 'react';
import { ConfirmDialog } from '@/components/ui';
import { useSetUserStatus } from '@/hooks/useUsers';
import { toast } from '@/store/toastStore';
import type { UserSummary } from '@/types/api';
import { errorText } from '@/utils/errors';

/** Closing an account's access, or giving it back. */
export function UserAccessDialog({ user, onClose }: { readonly user: UserSummary | null; readonly onClose: () => void }): ReactNode {
  const setStatus = useSetUserStatus({
    onSuccess: (_result, variables) => {
      toast.success(variables.status === 'active' ? 'Доступ восстановлен' : 'Доступ закрыт');
      onClose();
    },
  });

  const closing = user?.status === 'active';

  return (
    <ConfirmDialog
      open={user !== null}
      title={closing ? 'Закрыть доступ?' : 'Восстановить доступ?'}
      description={
        closing
          ? `Под учётной записью ${user.login} больше нельзя будет войти, а её текущие сессии завершатся немедленно.`
          : user
            ? `Под учётной записью ${user.login} снова можно будет войти с прежним паролем.`
            : undefined
      }
      confirmLabel={closing ? 'Закрыть доступ' : 'Восстановить'}
      destructive={closing}
      busy={setStatus.isPending}
      error={setStatus.isError ? errorText(setStatus.error, 'Не удалось изменить доступ') : undefined}
      onConfirm={() => user && setStatus.mutate({ id: user.id, status: closing ? 'disabled' : 'active' })}
      onClose={onClose}
    />
  );
}
